import React, { useMemo, useState } from 'react';
import { Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, Typography } from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import SftGlobalData from '../../library/SftGlobalData';
import Colors from '../../library/Colors';

const filter = createFilterOptions();

/** The selected tags as the JSON text logging_records.tags stores: ["Voorraad","Printer: Brother-QL"]. */
export const tagsJson = (selected) => JSON.stringify((selected || []).map((option) => option.tag));

const sameTag = (a, b) => a.tag === b.tag && a.parent === b.parent;

/**
 * Tag field of the log screens. When the typed name does not exist yet, the last option adds it
 * as a new tag: support picks one of the existing categories and the tag is saved for everyone
 * in the region at once (logging&action=addLogTag). A name that already exists in another
 * category is not added again; that existing tag is selected instead.
 */
const LoggingTagField = ({ t, theme, tags, value, onChange, onTagAdded, error, onBlur, className, sx, userId, sft }) => {
    const [draft, setDraft] = useState(null);   // { tag, parent } while the add dialog is open
    const [busy, setBusy] = useState(false);
    const [dialogError, setDialogError] = useState('');

    const categories = useMemo(
        () => Array.from(new Set((tags || []).map((option) => option.parent).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
        [tags]
    );

    const filterOptions = (options, state) => {
        const filtered = filter(options, state);
        const typed = state.inputValue.trim();
        if (typed !== '' && !options.some((option) => option.tag.toLowerCase() === typed.toLowerCase())) {
            filtered.push({ isNew: true, tag: typed, parent: '' });
        }
        return filtered;
    };

    const handleChange = (event, next) => {
        const added = next.find((option) => option.isNew);
        if (added) {
            setDialogError('');
            setDraft({ tag: added.tag, parent: '' });
            onChange(next.filter((option) => !option.isNew));
            return;
        }
        onChange(next);
    };

    const errorText = (key) => ({
        logging_tag_unknown_category: t('log_tag_err_category', 'Choose one of the existing categories.'),
        logging_tag_invalid_tag: t('log_tag_err_name', 'Use a name of at most 64 characters, without quotes, square brackets or backslashes.'),
        logging_tag_not_allowed: t('log_tag_err_not_allowed', 'Only SoftTouch employees can add tags.'),
    }[key] || t('log_tag_err', 'The tag could not be added. Try again.'));

    const save = () => {
        setBusy(true);
        setDialogError('');
        SftGlobalData.post('logging&action=addLogTag', { sft_user_id: userId, sft, sft_id: 0, tag: draft.tag, parent: draft.parent }).then((response) => {
            setBusy(false);
            if (!response?.success || !response.data) {
                setDialogError(errorText(response?.translation_key));
                return;
            }
            const tag = { id: response.data.id, parent: response.data.parent, tag: response.data.tag };
            if (!response.data.existing && onTagAdded) onTagAdded(tag);
            if (!value.some((option) => sameTag(option, tag))) onChange([...value, tag]);
            setDraft(null);
        });
    };

    const fieldColor = theme === 'light' ? Colors.light_text : Colors.dark_text;

    return (
        <>
            <Autocomplete
                multiple
                id="tags-outlined"
                className={className}
                size={'small'}
                sx={sx}
                options={tags || []}
                value={value}
                onChange={handleChange}
                filterOptions={filterOptions}
                isOptionEqualToValue={(option, val) => sameTag(option, val)}
                getOptionLabel={(option) => (option.isNew ? option.tag : option.tag + ' - ' + option.parent)}
                renderOption={(props, option) => {
                    const { key, ...rest } = props;
                    return option.isNew ? (
                        <li key={key} {...rest} style={{ color: Colors.sft_blue, fontWeight: 600 }}>
                            {t('log_tag_add_option', { name: option.tag, defaultValue: '+ Add "{{name}}" as a new tag' })}
                        </li>
                    ) : (
                        <li key={key} {...rest}>{option.tag + ' - ' + option.parent}</li>
                    );
                }}
                renderInput={(params) => (
                    <TextField {...params} error={error} onBlur={onBlur} variant="outlined" label={t('tags')} required />
                )}
            />

            <Dialog open={!!draft} onClose={busy ? undefined : () => setDraft(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>{t('log_tag_new_title', 'New tag')}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
                    <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mb: 0.5 }}>
                        {t('log_tag_new_sub', 'The tag is available to all your colleagues right away.')}
                    </Typography>
                    <TextField size="small" label={t('log_tag_name', 'Name')} value={draft?.tag || ''} inputProps={{ maxLength: 64 }}
                        onChange={(e) => setDraft({ ...draft, tag: e.target.value })} sx={{ '& .MuiOutlinedInput-input': { color: fieldColor } }} />
                    <TextField select size="small" label={t('log_tag_category', 'Category')} value={draft?.parent || ''}
                        onChange={(e) => setDraft({ ...draft, parent: e.target.value })} autoFocus>
                        {categories.map((category) => <MenuItem key={category} value={category}>{category}</MenuItem>)}
                    </TextField>
                    {dialogError && <Typography sx={{ fontSize: 13.5, color: Colors.def_red }}>{dialogError}</Typography>}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDraft(null)} disabled={busy} sx={{ textTransform: 'none' }}>{t('cancel', 'Cancel')}</Button>
                    <Button variant="contained" disableElevation onClick={save} disabled={busy || !draft?.tag?.trim() || !draft?.parent} sx={{ textTransform: 'none' }}>
                        {t('log_tag_add', 'Add')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default LoggingTagField;
